// install.js — proc-v3 (F2.3c Fornitori + F2.3d Prodotti/Campagne + F2.3e
// Conversazioni/Ordini): WRAP ESTERNO flag-gated delle window.render<Vista>
// del monolite. File PURO (niente JSX, niente side-effect al load): è QUESTO
// che i contract test server/src/lib/proc-v3-*.test.js esercitano sotto
// node --test. Le factory React (create* dei rispettivi .jsx) arrivano
// per parametro: il grafo JSX resta fuori dai test.
//
// PUNTO DI INNESTO (studio f23c, alternativa (c) — decisione registrata):
// proc-v0.js è uno script CLASSICO: `function render<Vista>()` top-level è
// una proprietà scrivibile di window e OGNI chiamante interno (rerenderView,
// le bulk, i filtri) risolve l'identificatore sul global object A OGNI
// CHIAMATA. Sostituire window.render<Vista> intercetta TUTTI i redraw della
// vista — stesso pattern già in produzione dell'adapter (wrap di
// showView/rerenderAll, procurement-v0/adapter.js:136-156, protocollo 2.3
// "WRAP, non modifica"). proc-v0.js: ZERO byte cambiati → golden B2.5-bis
// INTATTO, nessun re-pin.
//
// GUARDIE (in ordine, ognuna ⇒ bundle INERTE, return false):
//  1. flag della vista !== '1' (DEFAULT OFF, flags.js) — a flag spenta il
//     wrap NON viene mai installato: mondo v1 runtime-identico;
//  2. shell v2 col bus assente (workspace-host legacy / bundle shell 404):
//     senza bus niente unmount su shell:navigate — meglio inerte;
//  3. modulo 'proc' mai registrato (adapter proc-v0 assente/rotto);
//  4. React 18 UMD assente (vendor mancante: senza createRoot niente v3);
//  5. window.render<Vista> non è una funzione (rename/refactor del monolite:
//     il contract test statico segnala il drift, qui si degrada a v1);
//  6. un global delegato manca (delegatedGlobals; per Prodotti anche i METODI
//     di window.confirmWrite, che è un oggetto — delegatedMethods): il v3
//     delega TUTTE le azioni al v1, senza anche uno solo la vista sarebbe
//     monca ⇒ inerte;
//  7. factory invalida (senza render/unmount/isMounted).
//
// CICLO DI VITA (identico per ogni vista):
//  - wrapper: v3.render() nel container; QUALUNQUE errore ⇒ console.error +
//    unmount + fallback al v1 originale (mai vista bianca, mai silenzio);
//  - unmount su shell:navigate {module:'proc', view!==<vista>} (bus
//    replay:false — mai eventi stantii): uscendo dalla vista il container
//    torna libero; il cambio MODULO non emette shell:navigate di proc e NON
//    smonta (il DOM del monolite viene solo nascosto: al rientro nel modulo
//    nessun rerenderView automatico — smontare lascerebbe la vista BIANCA);
//  - catch-up: se la vista è GIÀ attiva all'install (ordine di caricamento
//    anomalo), un giro di wrapper riallinea il DOM.
//
// BADGE — MAI 'nav:badge' da qui o da client/src/proc-v3/** (emettitore unico:
// adapter proc-v0). Asserito dai contract test.

import {
  procV3SuppliersEnabled, procV3ProductsEnabled, procV3CampaignsEnabled,
  procV3ConversationsEnabled, procV3OrdersEnabled,
  procV3TagsEnabled, procV3WarehouseEnabled,
  procV3MonitoringEnabled, procV3TemplatesEnabled,
  procV3CategoriesEnabled,
} from '../shell/flags.js';
import {
  DELEGATED_GLOBALS,
  PROD_DELEGATED_GLOBALS, PROD_DELEGATED_METHODS,
  CMP_DELEGATED_GLOBALS,
  CONV_DELEGATED_GLOBALS, CONV_DELEGATED_METHODS,
  ORD_DELEGATED_GLOBALS, ORD_DELEGATED_METHODS,
  TAG_DELEGATED_GLOBALS, TAG_DELEGATED_METHODS,
  WH_DELEGATED_GLOBALS, WH_DELEGATED_METHODS,
  MON_DELEGATED_GLOBALS, MON_DELEGATED_METHODS,
  TPL_DELEGATED_GLOBALS, TPL_DELEGATED_METHODS,
  CAT_DELEGATED_GLOBALS, CAT_DELEGATED_METHODS,
} from './api.js';

// Motore condiviso del wrap (F2.3d): le tre viste differiscono SOLO per
// flag, nome della render v1, id vista e deleghe — il ciclo di vita è uno.
function installProcV3View(win, createV3, spec) {
  if (!win || !spec.enabled(win)) return false; // flag OFF (default): inerte
  const W = win.Workspaces;
  if (!W || W.version !== 2 || !W.bus || typeof W.bus.on !== 'function' || typeof W.has !== 'function') return false;
  if (!W.has('proc')) return false; // adapter proc-v0 mai registrato: mai wrap orfano
  if (!win.React || !win.ReactDOM || typeof win.ReactDOM.createRoot !== 'function') return false;
  if (typeof win[spec.renderName] !== 'function' || win[spec.renderName].__procV3Wrapped) return false;
  for (const g of spec.delegatedGlobals) {
    if (typeof win[g] !== 'function') return false; // v1 monco: la vista delega tutto lì
  }
  for (const objName of Object.keys(spec.delegatedMethods || {})) {
    const obj = win[objName];
    if (!obj) return false;
    for (const m of spec.delegatedMethods[objName]) {
      if (typeof obj[m] !== 'function') return false;
    }
  }
  const v3 = typeof createV3 === 'function' ? createV3(win) : null;
  if (!v3 || typeof v3.render !== 'function' || typeof v3.unmount !== 'function' || typeof v3.isMounted !== 'function') return false;

  const orig = win[spec.renderName];
  const wrapped = function () {
    try {
      v3.render();
    } catch (e) {
      // mai vista bianca: si dichiara e si degrada al v1 originale
      console.error('[proc-v3] render ' + spec.labelIt + ' fallito — fallback al v1:', e);
      try { v3.unmount(); } catch (e2) { /* root già andato */ }
      return orig.apply(this, arguments);
    }
  };
  wrapped.__procV3Wrapped = true;
  wrapped.__procV3Orig = orig; // ispezionabile in collaudo (parità/rollback)
  win[spec.renderName] = wrapped;

  // Uscita dalla vista (navigazione interna al modulo proc): container libero.
  W.bus.on('shell:navigate', (p) => {
    if (p && p.module === 'proc' && p.view && p.view !== spec.viewId && v3.isMounted()) {
      try { v3.unmount(); } catch (e) { console.error('[proc-v3] unmount fallito:', e); }
    }
  }, { replay: false });

  // Catch-up: vista già attiva all'install ⇒ riallinea subito il DOM.
  if (win.currentView === spec.viewId) {
    try { win[spec.renderName](); } catch (e) { console.error('[proc-v3] catch-up fallito:', e); }
  }

  return true;
}

// F2.3c — lista "Fornitori" (wrap di window.renderSuppliers).
export function installProcV3Suppliers(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3SuppliersEnabled,
    renderName: 'renderSuppliers',
    viewId: 'suppliers',
    delegatedGlobals: DELEGATED_GLOBALS,
    labelIt: 'Fornitori',
  });
}

// F2.3d — lista "Prodotti" (wrap di window.renderProducts). confirmWrite è un
// OGGETTO su window (proc-v0.js:4221): guardia sui suoi metodi usati.
export function installProcV3Products(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3ProductsEnabled,
    renderName: 'renderProducts',
    viewId: 'products',
    delegatedGlobals: PROD_DELEGATED_GLOBALS,
    delegatedMethods: PROD_DELEGATED_METHODS,
    labelIt: 'Prodotti',
  });
}

// F2.3d — lista "Campagne outreach" (wrap di window.renderCampaigns).
export function installProcV3Campaigns(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3CampaignsEnabled,
    renderName: 'renderCampaigns',
    viewId: 'campaigns',
    delegatedGlobals: CMP_DELEGATED_GLOBALS,
    labelIt: 'Campagne',
  });
}

// F2.3e — lista "Conversazioni" (wrap di window.renderConversations).
// L'accoppiamento temuto in ondata 19 sta FUORI dalla vista (badge nav =
// updateConvCounter, refresh = poller globale che risolve renderConversations
// sul global object a ogni giro ⇒ passa dal wrap): vedi studio in api.js.
// confirmWrite e store sono OGGETTI su window: guardia sui metodi usati.
export function installProcV3Conversations(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3ConversationsEnabled,
    renderName: 'renderConversations',
    viewId: 'conversations',
    delegatedGlobals: CONV_DELEGATED_GLOBALS,
    delegatedMethods: CONV_DELEGATED_METHODS,
    labelIt: 'Conversazioni',
  });
}

// F2.3e — lista "Ordini" (wrap di window.renderOrders). I totali restano
// win.store.orderTotal sugli ordini di orderOut (stessa fonte del v1).
export function installProcV3Orders(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3OrdersEnabled,
    renderName: 'renderOrders',
    viewId: 'orders',
    delegatedGlobals: ORD_DELEGATED_GLOBALS,
    delegatedMethods: ORD_DELEGATED_METHODS,
    labelIt: 'Ordini',
  });
}

// F2.3f — lista "Tag" (wrap di window.renderTags). confirmWrite e store sono
// OGGETTI su window: guardia sui metodi usati (createTag / listTags).
export function installProcV3Tags(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3TagsEnabled,
    renderName: 'renderTags',
    viewId: 'tags',
    delegatedGlobals: TAG_DELEGATED_GLOBALS,
    delegatedMethods: TAG_DELEGATED_METHODS,
    labelIt: 'Tag',
  });
}

// F2.3f — "Magazzino" (wrap di window.renderWarehouse). Il tab attivo resta
// window._whTab (globale condiviso col v1); il tab DDT delega al modulo v1
// window.WarehouseDDT che NON è guardia (può caricarsi dopo: fallback v1
// replicato a runtime, vedi api.js).
export function installProcV3Warehouse(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3WarehouseEnabled,
    renderName: 'renderWarehouse',
    viewId: 'warehouse',
    delegatedGlobals: WH_DELEGATED_GLOBALS,
    delegatedMethods: WH_DELEGATED_METHODS,
    labelIt: 'Magazzino',
  });
}

// F2.3g — "Monitoraggio" (wrap di window.renderMonitoring). Prima vista sul
// componente condiviso CardGrid. Le modali "Esegui ora"/"Cronologia"/
// "+ Nuova pianificazione" sono DELEGHE alle globali v1 (guardie); toggle ed
// elimina passano da confirmWrite.schedule/deleteSchedule (metodi in guardia).
export function installProcV3Monitoring(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3MonitoringEnabled,
    renderName: 'renderMonitoring',
    viewId: 'monitoring',
    delegatedGlobals: MON_DELEGATED_GLOBALS,
    delegatedMethods: MON_DELEGATED_METHODS,
    labelIt: 'Monitoraggio',
  });
}

// F2.3g — "Templates" / Libreria modelli (wrap di window.renderTemplates).
// Seconda vista su CardGrid. Il modale del template è DELEGA a
// openTemplateModal (guardia); lettura da store.listTemplates (metodo in
// guardia). Lo stato di vista (ricerca/collassi) vive nella closure della
// factory: stesso ciclo di vita dei binding lessicali del v1.
export function installProcV3Templates(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3TemplatesEnabled,
    renderName: 'renderTemplates',
    viewId: 'templates',
    delegatedGlobals: TPL_DELEGATED_GLOBALS,
    delegatedMethods: TPL_DELEGATED_METHODS,
    labelIt: 'Templates',
  });
}

// F2.3h — "Categorie" (wrap di window.renderCategories). Prima vista sul
// componente condiviso TreeView. Le modali (prodotto/fornitore/preventivo/AI)
// e il picker fornitore sono DELEGHE alle globali v1 (guardie); le scritture
// categoria passano da confirmWrite.createCategory/renameCategory/
// deleteCategory/moveCategory + supplier/createTag/createSupplier (metodi in
// guardia; createSupplier serve all'inline-create del picker, replicato da
// quickCreate.supplier che NON è su window — const lessicale v1:4215).
// I Set di ricerca (catSearchManualExpanded/Collapsed/Revealed) e la
// clipboard (_catClipboard) sono GLOBALI su window: CONDIVISI col v1, non in
// guardia (proc-v0 li inizializza al load; la factory ha il fallback).
// CONFINE DICHIARATO: il drag&drop resta SOLO nel v1 (mandato f23h) — lo
// spostamento in v3 passa da Taglia/Incolla (stessa primitiva moveCategory).
export function installProcV3Categories(win, createV3) {
  return installProcV3View(win, createV3, {
    enabled: procV3CategoriesEnabled,
    renderName: 'renderCategories',
    viewId: 'categories',
    delegatedGlobals: CAT_DELEGATED_GLOBALS,
    delegatedMethods: CAT_DELEGATED_METHODS,
    labelIt: 'Categorie',
  });
}
